/**
 * Base Form Component
 * ===================
 * 
 * Abstract base class that provides comprehensive form functionality for all form components.
 * This component implements the Template Method pattern, defining the complete form workflow
 * while allowing child components to customize specific aspects.
 * 
 * Purpose:
 * - Provides complete form lifecycle management (initialization, validation, submission)
 * - Implements step-by-step navigation with validation
 * - Handles attachment and signature management
 * - Manages form state, loading, and error handling
 * - Provides consistent form submission workflow
 *  - Validates user session on component initialization to prevent users from filling out
 *   entire forms only to discover authentication issues during HTTP submission
 * 
 * 
 * Architecture Pattern:
 * - Template Method Pattern: Defines the overall form workflow
 * - Strategy Pattern: Allows child components to customize validation rules
 * - Observer Pattern: Uses Angular signals for reactive state management
 * 
 * Relationship with Other Components:
 * - Individual form components EXTEND this class to inherit all functionality
 * - FormLayoutComponent is used as a CHILD component for UI navigation
 * - This component manages the form logic, FormLayoutComponent handles the UI
 * 
 * Key Responsibilities:
 * 1. Form initialization and setup
 * 2. Step navigation and validation
 * 3. Attachment and signature management
 * 4. Form submission and error handling
 * 5. State management and progress tracking
 * 
 * Child Component Requirements:
 * - Must implement initializeForm() to set up form controls
 * - Must implement getFormData() to provide form data for submission
 * - Must set formType, formMetadata, model, and steps properties
 * - Can override setupDefaultStepValidation() for custom step validation
 * 
 * @template T The type of the form data model
 * @author MacSafety Development Team
 * @version 1.0
 */

import { Component, OnInit, signal, Input, ContentChild, TemplateRef } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { trigger, transition, style, animate } from '@angular/animations';
import { EntraService } from '../../../services/entra.service';
import { FormService } from '../../../services/form.service';

import { AttachmentUploadService } from '../../../services/attachment-upload.service';
import { AttachmentUploadData } from '../../../models/forms/attachments/attachment-upload';
import { FormSubmissionData } from '../../../models/forms/form-submission.model';
import { FormAttachmentUploadRequirements } from '../../../models/forms/attachments/form-attachment-requirements';
import {  FormSubmissionMetaData } from '../../../models/forms/general-form-data.model';
import { ToastService } from '../../../services/toast.service';
import { SignatureService } from '../../../services/signature.service';
import { FormStep } from './form-layout.component';
import { ScreenSizeService } from '../../../services/screen-size.service';
import { getRequiredFields } from '../../../utils/form-validators';
import { FormMetadata} from '../../../models/form-registry';
import { environment } from '../../../../environments/environment';

/**
 * Options for step validation including attachment and signature requirements.
 * Used to specify what files and signatures are required for each step.
 */
export interface StepValidationUploadData {
  /** Array of attachment requirement indices for this step */
  attachmentIndex?: Array<number>;
  /** Array of signature requirement indices for this step */
  signatureIndex?: Array<number>;
}

/**
 * Base form component that provides common functionality for all form components.
 * This abstract class is extended by specific form components to inherit
 * complete form management capabilities.
 * 
 * @template T The type of the form data model
 */
@Component({
  selector: 'app-base-form',
  template: '',
  styles: [`

    ion-row {
      margin-bottom: 40px !important;
    }

  `],
  animations: [
    trigger('fadeInOut', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('300ms ease-in', style({ opacity: 1 }))
      ]),
      transition(':leave', [
        animate('300ms ease-out', style({ opacity: 0 }))
      ])
    ])
  ]
})
export abstract class BaseFormComponent<T> implements OnInit {
  // ============================================================================
  // ABSTRACT PROPERTIES - MUST BE IMPLEMENTED BY CHILD COMPONENTS
  // ============================================================================
  // These properties define the form's identity and structure.
  // Child components must set these to their specific values.

  /** 
   * The form type identifier.
   * Child components must set this to their specific form type.
   * Used for form submission, routing, and metadata lookup.
   */
  protected formType!: string;

  /** 
   * The form metadata containing field mappings, attachment requirements, etc.
   * Child components must set this to their specific form metadata.
   * Retrieved from FormModelService during initialization.
   */
  protected formMetadata!: FormMetadata;

  /** 
   * The form data model instance.
   * Child components must set this to their specific model type.
   * Used for form initialization and data submission.
   */
  protected model!: T;

  /** 
   * The main form group containing all form controls.
   * Child components can extend this with additional form controls.
   * Managed by the base component for validation and submission.
   */
  protected form: FormGroup = new FormGroup({});

  /** 
   * Array of form steps defining the multi-step structure.
   * Child components must define their form steps.
   * Used for navigation, validation, and progress tracking.
   */
  protected steps: FormStep[] = [];

  // ============================================================================
  // INTERNAL PROPERTIES - MANAGED BY BASE COMPONENT
  // ============================================================================
  // These properties are used internally by the base component and
  // shared with child components for form management.

  /** 
   * Mapping of field names to their required status.
   * Set automatically by base component during initialization.
   * Used for validation and error display.
   */
  protected requiredFields!: { [key: string]: boolean };

  /** 
   * The form field mappings from metadata.
   * Child components must set this to their specific form field mappings.
   * Used for field label display and validation error messages.
   */
  protected fieldMappings!: Record<string, string>;

  /** 
   * The form attachment requirements from metadata.
   * Child components must set this to their specific form attachment requirements.
   * Used for validation and attachment management.
   */
  protected formAttachmentRequirements!: FormAttachmentUploadRequirements;

  /** 
   * The form title for display purposes.
   * Child components must set this to their specific form title.
   * Used in headers and navigation.
   */
  protected formTitle!: string;

  /** 
   * The form icon for display purposes.
   * Child components must set this to their specific form icon.
   * Used in headers and navigation.
   */
  protected formIcon!: string;

  /** 
   * Flag indicating if form is currently submitting.
   * Used internally by base component to prevent multiple submissions.
   * Set to true during submission process.
   */
  protected submitting = false;

  /** 
   * Flag indicating if attachment modal is open.
   * Used internally by base component for modal state management.
   * Prevents multiple modals from opening simultaneously.
   */
  protected isAttachmentModalOpen = false;

  /** 
   * Currently selected attachment for editing/viewing.
   * Used internally by base component for attachment management.
   * Set when user selects an attachment for modification.
   */
  protected selectedAttachment: AttachmentUploadData | null = null;

  /** 
   * Flag indicating if device is mobile.
   * Set automatically by base component during initialization.
   * Used for responsive behavior and mobile-specific features.
   */
  protected isMobile!: boolean;

  /** 
   * Signal tracking current step identifier.
   * Used internally by base component for step management.
   * Reactive signal that updates UI when step changes.
   */
  protected currentStep = signal<string>('');

  /** 
   * Signal tracking visited steps for navigation history.
   * Used internally by base component for validation and navigation.
   * Tracks which steps the user has accessed.
   */
  protected visitedSteps = signal<Set<string>>(new Set());

  /** 
   * Signal tracking completed steps for progress display.
   * Used internally by base component for UI display and validation.
   * Steps are marked complete when successfully navigated from.
   */
  protected completedSteps = signal<Set<string>>(new Set());

  /** 
   * Validation rules for each step.
   * Child components can add custom validation rules.
   * Used for step-by-step validation during navigation.
   */
  protected stepValidationRules: Record<string, () => boolean> = {};

  /** 
   * Getter for attachments signal from attachment service.
   * Used internally by base component for attachment management.
   * Provides reactive access to current attachments.
   */
  protected get attachments() {
    return this.attachmentService.attachments();
  }

  constructor(
    protected fb: FormBuilder,
    protected formService: FormService,
    protected router: Router,
    protected entraService: EntraService,
    protected attachmentService: AttachmentUploadService,
    protected toastService: ToastService,
    protected signatureService: SignatureService,
    protected screenSizeService: ScreenSizeService,
  ) {
    this.form = this.fb.group({})
    this.formService.resetDataLoading();

  }

  // ============================================================================
  // ABSTRACT METHODS - MUST BE IMPLEMENTED BY CHILD COMPONENTS
  // ============================================================================
  // These methods define the contract that child components must fulfill.

  /**
   * Initializes the form with its controls and validators.
   * Child components must implement this to set up their specific form structure.
   * Called during component initialization after metadata is loaded.
   * 
   * Implementation should:
   * - Create form groups and controls
   * - Set initial values from the model
   * - Apply validators to form controls
   * - Set up nested form groups if needed
   */
  protected initializeForm(): void {
    throw new Error('Method not implemented.');
  }

  /**
   * Returns the form data for submission.
   * Child components must implement this to provide their specific form data.
   * Called during form submission to gather all form values.
   * 
   * Implementation should:
   * - Extract values from the form group
   * - Transform data as needed for submission
   * - Return data in the expected model format
   * 
   * @returns The form data ready for submission
   */
  protected getFormData(): T {
    throw new Error('Method not implemented.');
  }

  // ============================================================================
  // LIFECYCLE AND INITIALIZATION METHODS
  // ============================================================================
  // These methods handle component initialization and setup.

  /**
   * Lifecycle hook that initializes the form component.
   * Sets up form, steps, attachment requirements, and screen size detection.
   * Called automatically by Angular after component construction.
   * 
   * Initialization sequence:
 * 1. Validate user session to prevent access with invalid authentication
 * 2. Set first step as current and mark as visited
 * 3. Load form metadata and requirements
 * 4. Initialize form structure and validation
 * 5. Set up attachment and signature services
 * 6. Detect device type for responsive behavior
   */
  ngOnInit() {

    this.entraService.validateSession();

    // Initialize the first step as current and mark it as visited
    this.currentStep.set(this.steps[0].id);
    this.visitedSteps.set(new Set([...this.visitedSteps(), this.steps[0].id]));
    
    this.formTitle = this.formMetadata.title;
    this.formIcon = this.formMetadata.icon;
    this.fieldMappings = this.formMetadata.fieldMappings;
    this.formAttachmentRequirements = this.formMetadata.formAttachmentRequirements;

    this.initializeFormAndValidation();
    this.attachmentService.setAttachmentRequirements(this.formMetadata.formAttachmentRequirements);
    this.signatureService.setSignatureRequirements(this.formMetadata.formAttachmentRequirements);
    this.requiredFields = getRequiredFields(this.form);
    
    this.isMobile = this.screenSizeService.isMobileDevice();
  }

  /**
   * Initializes the form steps and sets up default validation.
   * Sets the first step as current if steps are defined.
   * Called during component initialization to set up step structure.
   */
  protected initializeSteps(): void {
    if (this.steps.length > 0) {
      this.currentStep.set(this.steps[0].id);
      // Mark first step as visited
      this.visitedSteps.set(new Set([this.steps[0].id]));
    }
    this.setupDefaultStepValidation();
  }

  /**
   * Initializes the form and validation system.
   * Sets up form structure, applies requirements, and initializes steps.
   * Called during component initialization to complete setup.
   */
  protected initializeFormAndValidation(): void {
    this.initializeForm();
    if (!this.shouldApplyFormRequirements()) {
      this.removeAllValidators();
    }
    this.initializeSteps();
  }

  /**
   * Sets up default validation rules for steps.
   * Child components can override this to provide custom validation rules.
   * Called during step initialization to set up validation system.
   * 
   * Default implementation provides no validation rules.
   * Child components should override to implement step-specific validation.
   */
  protected setupDefaultStepValidation(): void {
    // No default validation rules - let child components handle it
  }

  // ============================================================================
  // STEP NAVIGATION METHODS
  // ============================================================================
  // These methods handle step-by-step navigation with validation.

  /**
   * Handles navigation to the next step.
   * Validates the current step when attempting to proceed.
   * If validation fails, shows error message and stays on current step.
   * If validation passes, moves to next step and marks current step as completed.
   * Also re-validates all completed steps to ensure they're still valid.
   * 
   * Navigation flow:
   * 1. Re-validate all completed steps
   * 2. Validate current step
   * 3. Mark current step as completed if valid
   * 4. Move to next step
   * 5. Mark next step as visited
   */
  onNextStep(): void {
    const currentIndex = this.steps.findIndex(step => step.id === this.currentStep());
    if (currentIndex < this.steps.length - 1) {
      const currentStepId = this.currentStep();
      const nextStep = this.steps[currentIndex + 1];
      
      // Re-validate all completed steps first (in case user changed data after completing them)
      this.revalidateCompletedSteps();
      
      // Validate current step when attempting to proceed
      if (this.validateStepOnDemand(currentStepId)) {
        // Mark current step as completed
        this.completedSteps.set(new Set([...this.completedSteps(), currentStepId]));
        
        // Move to next step
        this.currentStep.set(nextStep.id);
        
        // Mark next step as visited
        this.visitedSteps.set(new Set([...this.visitedSteps(), nextStep.id]));
      } else {
        this.showStepValidationError(currentStepId);
      }
    }
  }

  /**
   * Handles navigation to the previous step.
   * Only proceeds if there is a previous step.
   * Also re-validates completed steps to ensure they're still valid.
   * 
   * Navigation flow:
   * 1. Re-validate all completed steps
   * 2. Check if previous step exists
   * 3. Move to previous step if available
   */
  onPreviousStep(): void {
    // Re-validate all completed steps first (in case user changed data after completing them)
    this.revalidateCompletedSteps();
    
    const currentIndex = this.steps.findIndex(step => step.id === this.currentStep());
    if (currentIndex > 0) {
      const previousStep = this.steps[currentIndex - 1];
      this.currentStep.set(previousStep.id);
    }
  }

  /**
   * Handles direct navigation to a specific step.
   * Only allows navigation to previous steps or the immediate next step.
   * Also re-validates completed steps to ensure they're still valid.
   * 
   * Navigation rules:
   * - Can always navigate to previous steps
   * - Can navigate to immediate next step if current step is valid
   * - Cannot skip steps (maintains form integrity)
   * 
   * @param targetStepId - The target step identifier
   */
  onStepChange(targetStepId: string): void {
    const targetIndex = this.steps.findIndex(step => step.id === targetStepId);
    const currentIndex = this.steps.findIndex(step => step.id === this.currentStep());
    
    // Re-validate all completed steps first (in case user changed data after completing them)
    this.revalidateCompletedSteps();
    
    // Allow navigation to previous steps
    if (targetIndex < currentIndex) {
      this.currentStep.set(targetStepId);
      return;
    }
    
    // Allow navigation to immediate next step if current step is valid
    if (targetIndex === currentIndex + 1) {
      this.onNextStep();
      return;
    }
    
    // Don't allow skipping steps
  }

  // ============================================================================
  // STEP STATE AND VALIDATION METHODS
  // ============================================================================
  // These methods provide step status information and validation.

  /**
   * Checks if a step is currently active.
   * Used by templates to highlight the current step.
   * 
   * @param stepId - The ID of the step to check
   * @returns true if the step is currently active
   */
  isStepActive(stepId: string): boolean {
    return this.currentStep() === stepId;
  }

  /**
   * Checks if a step is completed (has been successfully navigated from).
   * Used for progress indicators and step completion styling.
   * 
   * @param stepId - The ID of the step to check
   * @returns true if the step has been completed
   */
  isStepCompleted(stepId: string): boolean {
    return this.completedSteps().has(stepId);
  }

  /**
   * Checks if the current step is the first step.
   * Used to conditionally show/hide previous button.
   * 
   * @returns true if current step is the first step
   */
  isFirstStep(): boolean {
    return this.steps.length > 0 && this.currentStep() === this.steps[0].id;
  }

  /**
   * Checks if the current step is the last step.
   * Used to conditionally show/hide next button and show submit button.
   * 
   * @returns true if current step is the last step
   */
  isLastStep(): boolean {
    return this.steps.length > 0 && this.currentStep() === this.steps[this.steps.length - 1].id;
  }

  /**
   * Gets the current step number (1-based).
   * Used in progress indicators and step counters.
   * 
   * @returns The current step number (1, 2, 3, etc.)
   */
  getCurrentStepNumber(): number {
    const currentIndex = this.steps.findIndex(step => step.id === this.currentStep());
    return currentIndex + 1;
  }

  /**
   * Gets the total number of steps.
   * Used in progress bars and step counters.
   * 
   * @returns The total number of steps in the form
   */
  getTotalSteps(): number {
    return this.steps.length;
  }

  /**
   * Calculates the progress percentage through the form.
   * Used for progress bars and completion indicators.
   * 
   * @returns Progress percentage from 0 to 100
   */
  getProgressPercentage(): number {
    if (this.steps.length === 0) return 0;
    return (this.getCurrentStepNumber() / this.getTotalSteps()) * 100;
  }

  /**
   * Checks if navigation to a specific step is allowed.
   * Enforces step-by-step progression to maintain form integrity.
   * 
   * Navigation rules:
   * - Can always navigate to previous steps
   * - Can navigate to immediate next step
   * - Cannot skip steps
   * 
   * @param stepId - The ID of the step to check
   * @returns true if navigation to the step is allowed
   */
  canNavigateToStep(stepId: string): boolean {
    const targetIndex = this.steps.findIndex(step => step.id === stepId);
    const currentIndex = this.steps.findIndex(step => step.id === this.currentStep());
    
    // Can always navigate to previous steps
    if (targetIndex < currentIndex) return true;
    
    // Can navigate to next step (validation happens in onStepChange)
    if (targetIndex === currentIndex + 1) return true;
    
    // Cannot skip steps
    return false;
  }

  // ============================================================================
  // FORM VALIDATION AND SUBMISSION METHODS
  // ============================================================================
  // These methods handle form validation and submission workflow.

  /**
   * Checks if form requirements should be applied based on environment configuration.
   * Allows disabling form validation for development/testing purposes.
   * 
   * @returns true if form requirements should be applied, false otherwise
   */
  protected shouldApplyFormRequirements(): boolean {
    return !environment.noFormRequirements;
  }

  /**
   * Removes all validators from the form and its nested form groups.
   * This is used when noFormRequirements is true in the environment.
   * Useful for development and testing scenarios.
   */
  protected removeAllValidators(): void {
    if (!this.form) return;

    const removeValidatorsFromGroup = (formGroup: FormGroup): void => {
      Object.keys(formGroup.controls).forEach(key => {
        const control = formGroup.get(key);
        if (control) {
          control.clearValidators();
          control.updateValueAndValidity();
          
          // If it's a nested form group, recursively remove validators
          if (control instanceof FormGroup) {
            removeValidatorsFromGroup(control);
          }
        }
      });
    };

    removeValidatorsFromGroup(this.form);
  }

  /**
   * Checks if the entire form is valid for submission.
   * This is only called when submitting, not continuously.
   * 
   * For stepped forms, ensures all steps are completed.
   * For single forms, checks form validity.
   * 
   * @returns true if the form is ready for submission
   */
  formValid(): boolean {
    if (this.steps.length > 0) {
      // Check if all steps are completed
      return this.steps.every(step => this.isStepCompleted(step.id));
    }
    return this.form.valid;
  }

  /**
   * Gets the form submission metadata including submitter information.
   * Automatically populates submitter details from authentication service.
   * 
   * @returns FormSubmissionMetaData with submitter info and timestamp
   */
  getFormSubmissionMetaData(): FormSubmissionMetaData {
    return {
      submittedByName: this.entraService.userName(),
      submittedByEmail: this.entraService.userEmail(),
      submissionDate: new Date()
    };
  }

  /**
   * Updates the model properties with photos and signatures from the services.
   * Loops over the photos and signatures records and updates corresponding model properties
   * that are of type AttachmentArray.
   * 
   * This method integrates attachment and signature data into the form model
   * before submission, ensuring all file data is properly included.
   * 
   * @param formData - The form data to update with attachments
   * @returns Updated form data with attachment and signature information
   */
  protected updateModelWithAttachments(formData: T): T {
    // Get current photos and signatures from services
    const attachments = this.attachmentService.attachments();
    const signatures = this.signatureService.signatures();

    if (formData && typeof formData === 'object') {
      // Update model with attachments
      Object.keys(attachments).forEach(identifier => {
        const attachmentArray = attachments[identifier];
        if (attachmentArray && attachmentArray.length > 0 && identifier in formData) {
          const modelProperty = (formData as any)[identifier];
          if (Array.isArray(modelProperty)) {
            (formData as any)[identifier] = attachmentArray.map(attachment => attachment.name);
          }
        }
      });

      // Update model with signatures
      Object.keys(signatures).forEach(identifier => {
        const signatureArray = signatures[identifier];
        if (signatureArray && signatureArray.length > 0 && identifier in formData) {
          const modelProperty = (formData as any)[identifier];
          if (Array.isArray(modelProperty)) {
            (formData as any)[identifier] = signatureArray.map(signature => signature.fileName);
          }
        }
      });
    }
    return formData;
  }

  /**
   * Gets validation errors from the form.
   * Extracts error messages for display to the user.
   * 
   * @returns Array of validation error messages
   */
  private getValidationErrors(): Array<string> {
    const errors: Array<string> = [];
    Object.keys(this.form.controls).forEach(key => {
      const control = this.form.get(key);
      if (control?.errors) {
        const fieldName = this.formMetadata.fieldMappings[key] || key;
        Object.keys(control.errors).forEach(errorKey => {
          errors.push(`${fieldName}: ${errorKey}`);
        });
      }
    });
    return errors;
  }

  /**
   * Shows validation error for a specific step.
   * Displays user-friendly error message for step validation failures.
   * 
   * @param stepId - The ID of the step with validation errors
   */
  protected showStepValidationError(stepId: string): void {
    const step = this.steps.find(s => s.id === stepId);
    if (step) {
      this.toastService.showErrorMessage(`Please complete all required fields in ${step.title}`);
    }
  }

  /**
   * Validates a step on-demand (only when needed, not continuously).
   * Calls the step's validation rule if one is defined.
   * 
   * @param stepId - The ID of the step to validate
   * @returns true if the step is valid, false otherwise
   */
  protected validateStepOnDemand(stepId: string): boolean {
    const validationRule = this.stepValidationRules[stepId];
    return validationRule ? validationRule() : true;
  }

  /**
   * Handles form submission with proper validation and error handling.
   * Orchestrates the complete submission workflow including validation,
   * data preparation, submission, and cleanup.
   * 
   * Submission workflow:
   * 1. Validate form readiness
   * 2. Prepare submission data
   * 3. Submit form request
   * 4. Handle success/error
   * 5. Clean up resources
   */
  async onSubmit(): Promise<void> {
    try {
      // Validate form before submission
      if (!this.isFormReadyForSubmission()) {
        return;
      }

      this.submitting = true;
      
      // Prepare and submit form data
      const submissionRequest = await this.prepareSubmissionRequest();
      await this.submitFormRequest(submissionRequest);
      
      // Clean up after successful submission
      this.onSubmitCleanup();
      
    } catch (error) {
    //  await this.handleSubmissionError(error);
    this.submitting=false;
    } finally {
      this.submitting = false;
    }
  }

  /**
   * Validates that the form is ready for submission.
   * Ensures all required steps are completed and form is valid.
   * 
   * @returns true if the form is ready for submission
   */
  private isFormReadyForSubmission(): boolean {
    // For stepped forms, ensure all steps are completed
    if (this.steps.length > 0) {
      const currentStepId = this.currentStep();
      
      // Validate current step first
      if (!this.validateStepOnDemand(currentStepId)) {
        this.showStepValidationError(currentStepId);
        return false;
      }
      
      // Mark current step as completed if it's not already
      if (!this.isStepCompleted(currentStepId)) {
        this.completedSteps.set(new Set([...this.completedSteps(), currentStepId]));
      }
      
      // Check if all steps are completed
      const incompleteSteps = this.steps.filter(step => !this.isStepCompleted(step.id));
      if (incompleteSteps.length > 0) {
        this.showStepValidationError(incompleteSteps[0].id);
        return false;
      }
    }

    // Validate overall form
    if (!this.formValid()) {
      const errors = this.getValidationErrors();
      if (errors.length > 0) {
        this.toastService.showErrorMessage(errors.join('\n'));
      }
      return false;
    }

    return true;
  }

  /**
   * Prepares the complete submission request with form data, attachments, and signatures.
   * Gathers all necessary data and prepares it for submission to the backend.
   * 
   * @returns Promise resolving to the complete submission request
   */
  private async prepareSubmissionRequest(): Promise<FormSubmissionData<T>> {
    const formEntryData = this.getFormData();
    const formSubmissionMetaData = this.getFormSubmissionMetaData();
    const formDataWithSubmissionMetaData = { ...formEntryData, ...formSubmissionMetaData };
    
    // Prepare attachments and signatures as Files in parallel for better performance
    const [attachmentFiles, signatureFiles] = await Promise.all([
      this.attachmentService.prepareAttachmentsAsFiles(),
      this.attachmentService.prepareSignaturesAsFiles()
    ]);

    return {
      formType: this.formType,
      formEntryData: formDataWithSubmissionMetaData,
      attachments: {
        uploads: attachmentFiles,
        signatures: signatureFiles
      }
    };
  }

  /** 
   * Submits the form request to the service.
   * Delegates actual submission to FormService and handles navigation on success.
   * 
   * @param submissionRequest - The prepared submission request
   * @returns Promise that resolves when submission is complete
   */
  private async submitFormRequest(submissionRequest: FormSubmissionData<T>): Promise<void> {
    return new Promise((resolve, reject) => {
 const onSuccess = () => {
  resolve();
  this.router.navigate(
    ['/form-submission-confirmation', this.formType],
    { state: { warning: this.formService.formSubmissionResult()?.warning ?? null } }
  );
};
      // const onError = () => reject(new Error('Form submission failed'));
      console.log('submissionRequest', submissionRequest);
      
      // Prepare form metadata for submission
      const formMetadata = {
        formSteps: this.formMetadata.formSteps || [],
        fieldLabels: this.formMetadata.fieldMappings || {},
        formTitle: this.formMetadata.title || ''
      };
      
      this.formService.submitForm(submissionRequest, formMetadata, onSuccess);
    });
  }

  /**
   * Handles submission errors with proper logging and user feedback.
   * Logs error details and displays user-friendly error message.
   * Not currently used, as the form service handles the error.
   * 
   * @param error - The error that occurred during submission
   */
  private async handleSubmissionError(error: any): Promise<void> {
    console.error('Form submission failed:', error);
    
    const errorMessage = this.getErrorMessage(error);
    await this.toastService.showErrorMessage(errorMessage);
  }

  /**
   * Extracts a user-friendly error message from the error object.
   * Handles various error formats and provides fallback messages.
   * 
   * @param error - The error object to extract message from
   * @returns User-friendly error message
   */
  private getErrorMessage(error: any): string {
    if (error?.message) {
      return error.message;
    }
    
    if (error?.error?.message) {
      return error.error.message;
    }
    
    return 'An unexpected error occurred while submitting the form. Please try again.';
  }

  /**
   * Performs cleanup operations after form submission.
   * Clears attachments, signatures, and resets submission state.
   * Called after successful submission to clean up resources.
   */
  onSubmitCleanup(): void {
    this.attachmentService.clearAllAttachments();
    this.signatureService.clearSignatures();
    this.submitting = false;
  }

  // ============================================================================
  // STEP VALIDATION SETUP AND HELPER METHODS
  // ============================================================================
  // These methods provide step validation infrastructure and utilities.

  /**
   * Re-validates all completed steps to ensure they're still valid.
   * This is called only during navigation attempts, not continuously.
   * If a completed step is no longer valid, it's removed from the completed set.
   * 
   * This ensures that if a user changes data in a completed step,
   * the step is marked as incomplete until re-validated.
   */
  protected revalidateCompletedSteps(): void {
    const currentCompleted = this.completedSteps();
    const stillValidSteps = new Set<string>();
    
    // Check each completed step to see if it's still valid
    currentCompleted.forEach(stepId => {
      if (this.validateStepOnDemand(stepId)) {
        stillValidSteps.add(stepId);
      }
    });
    
    // Update completed steps to only include those that are still valid
    if (stillValidSteps.size !== currentCompleted.size) {
      this.completedSteps.set(stillValidSteps);
    }
  }

  /**
   * Sets a validation rule for a specific step.
   * Child components use this to define custom validation logic for each step.
   * 
   * @param stepId - The ID of the step
   * @param validationFn - The validation function that returns true if valid
   */
  protected setStepValidationRule(stepId: string, validationFn: () => boolean): void {
    this.stepValidationRules[stepId] = validationFn;
  }

  /**
   * Checks if all controls in the step's form group(s) are valid.
   * Marks any invalid controls as touched so validation errors show in UI.
   * 
   * @param stepFormGroup - Array of form groups to validate for the step
   * @returns true if all controls in the step are valid
   */
  protected isStepGroupValid(stepFormGroup: FormGroup[]): boolean {
    let allValid = true;
    
    stepFormGroup.forEach(formGroup => {
      if (formGroup instanceof FormGroup) {
        // Check all controls in the form group
        Object.keys(formGroup.controls).forEach(controlName => {
          const control = formGroup.get(controlName);
          if (!control?.valid) {
            control?.markAsTouched();
            allValid = false;
          }
        });
      }
    });
    
    return allValid;
  }

  /**
   * Sets up validation for a step.
   * Convenience method that combines validation rule setup with validation logic.
   * 
   * @param stepId - The ID of the step
   * @param formGroup - Form group to validate for the step
   * @param uploadData - Additional validation options for attachments/signatures
   */
  protected setupStepValidation(
    stepId: string,
    stepFormGroup: FormGroup,
    uploadData?: StepValidationUploadData
  ): void {
    this.setStepValidationRule(stepId, () => this.validateStep(stepId, stepFormGroup, uploadData));
  }

  /**
   * Validates a step based on its fields and requirements.
   * Comprehensive validation including form controls, attachments, and signatures.
   * 
   * @param stepId - The ID of the step
   * @param stepFormGroup - Form group to validate for the step
   * @param uploadData - Attachment and signature requirements for the step
   * @returns true if the step passes all validation requirements
   */
  protected validateStep(
    stepId: string,
    stepFormGroup: FormGroup,
    uploadData?: StepValidationUploadData
  ): boolean {
    let attachmentsValid: boolean = true;
    let signaturesValid: boolean = true;

    // Validate required attachments for this step
    if (uploadData?.attachmentIndex !== undefined && this.formAttachmentRequirements?.uploads !== undefined) {
      for (const attachmentIndex of uploadData.attachmentIndex) {
        const attachmentIdentifier = this.formAttachmentRequirements?.uploads[attachmentIndex]?.identifier;
        if (!this.attachmentService.hasUploads(attachmentIdentifier)) {
          attachmentsValid = false;
        }
      }
    }

    // Validate required signatures for this step
    if (uploadData?.signatureIndex !== undefined && this.formAttachmentRequirements?.signatures !== undefined) {
      for (const signatureIndex of uploadData.signatureIndex) {
        const signatureIdentifier = this.formAttachmentRequirements?.signatures[signatureIndex]?.identifier;
        if (!this.signatureService.hasSignature(signatureIdentifier)) {
          signaturesValid = false;
        }
      }
    }

    // Recursively mark all controls as touched
    const markControlsAsTouched = (group: FormGroup) => {
      Object.keys(group.controls).forEach(key => {
        const control = group.get(key);
        if (control instanceof FormGroup) {
          markControlsAsTouched(control);
        } else if (control) {
          control.markAsTouched();
        }
      });
    };

    markControlsAsTouched(stepFormGroup);
    stepFormGroup.markAsTouched();

    const fieldsValid = stepFormGroup.valid;
    
    return fieldsValid && attachmentsValid && signaturesValid;
  }
}